import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  DistributorsIcon, SuppliersIcon, DecoratorsIcon,
  PrintShopsIcon, SignCompaniesIcon, QuickBooksIcon,
} from "./SolutionIcons";
import { useSiteSettings } from "@/data/useSiteSettings";
import { SOLUTIONS_OVERVIEW_DEFAULT, mergeSection } from "@/data/homepageSectionDefaults";

// Icons stay hardcoded (custom SVG) - they map to card slots by position.
// The admin can edit title/desc/link per slot; icon per slot is fixed.
const ICONS = [
  { Icon: DistributorsIcon,   accent: "from-sky-400 to-blue-600" },
  { Icon: SuppliersIcon,      accent: "from-blue-500 to-indigo-500" },
  { Icon: DecoratorsIcon,     accent: "from-indigo-500 to-violet-500" },
  { Icon: PrintShopsIcon,     accent: "from-sky-400 to-cyan-500" },
  { Icon: SignCompaniesIcon,  accent: "from-cyan-500 to-teal-500" },
  { Icon: QuickBooksIcon,     accent: "from-teal-500 to-emerald-500" },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function SolutionsOverview({ data }) {
  const settings = useSiteSettings();
  const merged = mergeSection(SOLUTIONS_OVERVIEW_DEFAULT, settings?.homepage?.solutions_overview);
  const c = mergeSection(merged, data);

  return (
    <section data-testid="solutions-overview" className="pt-6 pb-14 lg:pt-8 lg:pb-20 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 lg:mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            {c.heading}
          </h2>
          <p className="mt-4 text-base lg:text-lg text-slate-500 leading-relaxed">
            {c.intro}
          </p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {c.cards.map((card, i) => {
            const icon = ICONS[i] || ICONS[ICONS.length - 1];
            return (
              <motion.div key={`${card.title}-${i}`} variants={cardVariants}>
                <Link
                  to={card.link}
                  data-testid={`solution-card-${card.title.toLowerCase().replace(/\s/g, "-")}`}
                  className="group block bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300"
                >
                  {/* Gradient top accent bar */}
                  <div className={`h-1 bg-gradient-to-r ${icon.accent}`} />

                  {/* Icon block: centered, generous whitespace, no fill container */}
                  <div className="pt-6 sm:pt-8 pb-3 sm:pb-4 flex justify-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 group-hover:scale-105 transition-transform duration-300">
                      <icon.Icon />
                    </div>
                  </div>

                  {/* Text block */}
                  <div className="px-5 sm:px-6 pb-6 sm:pb-8 text-center">
                    <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-sky-600 transition-colors">
                      {card.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed">
                      {card.desc}
                    </p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        <div className="text-center mt-10">
          <Link
            to="/solutions"
            data-testid="solutions-see-all"
            className="text-sm font-semibold text-sky-600 hover:text-sky-700 transition-colors"
          >
            {c.view_all_label} &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
}
